import {useLegacyUser} from '@deezer/react-user';
import type {Trackables} from '@app/types/tracking';
import {useRouter} from 'next/router';
import {useCallback, useEffect} from 'react';
import type React from 'react';
import {pushEvent} from '@deezer/tracking';
import {getClientSideUniqId} from '../abTest';
import {toGAEvent} from './helpers/cdpPageView';

type TrackingHook<T = Trackables> = {
	Track: React.FC<React.PropsWithChildren>;
	trackEvent: (data?: Partial<T>) => void;
	getTrackingData: () => Partial<T>;
};

function removePropertyFromObject<T extends Record<string, unknown>>(
	obj: T,
	property: string,
): Partial<T> {
	const {[property]: _, ...rest} = obj;
	return rest as Partial<T>;
}

/**
 * Helper hook to ease pageview tracking and inject common data into the data layer
 * @deprecated - do not use this hook directly, it's used to better separate code in the tracking provider
 */
export const usePageTracking = (
	options: Partial<Trackables> = {},
): TrackingHook<Trackables> => {
	const {locale} = useRouter();
	const cleanedOptions = removePropertyFromObject(options, 'cdpViewId');
	const {pagename, ...rest} = cleanedOptions;

	const trackingOptions = {
		pagecategory: 'explore',
		pagename,
		language: locale,
		/**
		 * This allowes to forward a default eventcategory for components shared between
		 * multiple pages. Most categories are the pagename anyway and it does not change
		 * anything if you want to specify another one then you can!
		 */
		eventcategory: pagename,
		...rest,
	};

	const [legacyUser] = useLegacyUser();

	// Custom tracking hook using @deezer/tracking
	const Track: React.FC<React.PropsWithChildren> = useCallback(
		({children}) => children as React.ReactElement,
		[],
	);

	const trackingHook: TrackingHook<Trackables> = {
		Track,
		trackEvent: (data?: Partial<Trackables>) => {
			pushEvent(toGAEvent({...trackingOptions, ...data}));
		},
		getTrackingData: () => trackingOptions,
	};
	// Injects page / user data into the datalayer
	useEffect(() => {
		if (legacyUser) {
			trackingHook.trackEvent({
				event: 'pageView',
				dzr_uniq_id: getClientSideUniqId(),
				deezer_user_id: String(legacyUser.USER?.USER_ID),
				country: legacyUser.COUNTRY,
				new_user: Number(legacyUser.USER?.NEW_USER),
				offer_id: Number(legacyUser.OFFER_ID),
				sub: 0,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [legacyUser]);

	return trackingHook;
};
